import {
  CalendarDays,
  CalendarRange,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Clock3,
  MapPin,
  PencilLine,
  Repeat2,
  RefreshCw,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { getEventId } from '../../lib/appHelpers';
import { formatRecurrence } from '../../lib/recurrence';
import {
  formatRoutineRepeat,
  getRoutineTone,
  projectRoutineTasksForDay,
} from '../../lib/routines';
import RecurrenceEditor from '../ui/RecurrenceEditor';
import SegmentedTabs from '../ui/SegmentedTabs';
import {
  EmptyState,
  inputClassName,
  MetricCard,
  PageHero,
  SectionCard,
  SectionTitle,
  StatusPill,
} from '../ui/ScreenPrimitives';

const DAY_MS = 86400000;

function padNumber(value) {
  return String(value).padStart(2, '0');
}

function formatDateKey(date) {
  return [
    date.getFullYear(),
    padNumber(date.getMonth() + 1),
    padNumber(date.getDate()),
  ].join('-');
}

function parseDateKey(value) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(String(value).trim())) {
    return null;
  }

  const [year, month, day] = String(value).split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date, days) {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
}

function getWeekStart(date) {
  const day = startOfDay(date);
  day.setDate(day.getDate() - day.getDay());
  return day;
}

function getEventDate(event) {
  if (!event?.startAt) return null;
  const date = new Date(event.startAt);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getEventDateKey(event) {
  const date = getEventDate(event);
  return date ? formatDateKey(date) : '';
}

function formatEventDate(startAt, endAt, allDay) {
  if (!startAt) return { dateLabel: 'No date', timeLabel: '', rangeLabel: '', kind: 'future' };

  const start = new Date(startAt);
  if (Number.isNaN(start.getTime())) {
    return { dateLabel: startAt, timeLabel: '', rangeLabel: '', kind: 'future' };
  }

  const now = new Date();
  const today = startOfDay(now);
  const startDay = startOfDay(start);
  const diffDays = Math.ceil((startDay - today) / DAY_MS);

  const dateLabel = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(start);
  const timeLabel = allDay
    ? 'All day'
    : new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(start);

  let rangeLabel = '';
  if (!allDay && endAt) {
    const end = new Date(endAt);
    if (!Number.isNaN(end.getTime())) {
      rangeLabel = `to ${new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(end)}`;
    }
  }

  let kind = 'future';
  if (diffDays < 0) kind = 'past';
  else if (diffDays === 0) kind = 'today';
  else if (diffDays <= 3) kind = 'soon';

  return { dateLabel, timeLabel, rangeLabel, kind };
}

function formatWeekRange(start, end) {
  const monthFormatter = new Intl.DateTimeFormat(undefined, { month: 'short' });
  const startMonth = monthFormatter.format(start);
  const endMonth = monthFormatter.format(end);
  const yearFormatter = new Intl.DateTimeFormat(undefined, { year: 'numeric' });
  const yearLabel = yearFormatter.format(end);

  if (startMonth === endMonth) {
    return `${startMonth} ${start.getDate()}-${end.getDate()}, ${yearLabel}`;
  }

  return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}, ${yearLabel}`;
}

function ScopeBadge({ scope }) {
  return (
    <StatusPill tone={scope === 'family' ? 'family' : 'accent'}>
      {scope === 'family' ? 'Family shared' : 'Personal'}
    </StatusPill>
  );
}

function EventCard({ event, calendarParams, onUpdateEvent, onEditEvent, onDeleteEvent }) {
  const eventId = getEventId(event);
  const isFocused = eventId && eventId === calendarParams?.focusEventId;
  const { dateLabel, timeLabel, rangeLabel, kind } = formatEventDate(event?.startAt, event?.endAt, event?.allDay);
  const isPast = event?.status === 'completed' || event?.status === 'cancelled' || kind === 'past';
  const recurrenceLabel = formatRecurrence(event?.recurrence);
  const dateTone =
    kind === 'today' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
    kind === 'soon' ? 'bg-blue-50 text-blue-700 border-blue-200' :
    kind === 'past' ? 'bg-slate-100 text-slate-500 border-slate-200' :
    'bg-app-surface-alt text-app-ink border-app-border/70';
  const statusTone =
    event?.status === 'completed' ? 'success' :
    event?.status === 'cancelled' ? 'error' :
    'accent';

  return (
    <article
      className={cn(
        'flex gap-4 rounded-[22px] border p-5 shadow-subtle transition lg:p-6',
        isFocused ? 'border-app-accent/35 bg-blue-50/70 shadow-panel' : 'border-app-border/80 bg-app-surface/95',
        isPast && 'opacity-80'
      )}
      data-focus-id={eventId ? `event-${eventId}` : undefined}
    >
      <div className={cn('flex min-w-[88px] shrink-0 flex-col items-center justify-center rounded-[18px] border px-3 py-4 text-center', dateTone)}>
        <span className="text-sm font-semibold">{dateLabel}</span>
        <span className="mt-1 text-xs font-medium">{timeLabel}</span>
        {rangeLabel ? <span className="mt-1 text-[11px]">{rangeLabel}</span> : null}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <ScopeBadge scope={event?.scope || 'personal'} />
          <StatusPill tone={statusTone}>{event?.status || 'scheduled'}</StatusPill>
          {event?.allDay ? <StatusPill tone="neutral">All day</StatusPill> : null}
          {event?.isGeneratedOccurrence ? <StatusPill tone="accent">Occurrence</StatusPill> : null}
          {recurrenceLabel ? <StatusPill tone="neutral">{recurrenceLabel}</StatusPill> : null}
        </div>

        <p className="mt-3 text-lg font-semibold tracking-tight text-app-ink">{event?.title || 'Untitled event'}</p>
        {event?.description ? <p className="mt-2 text-sm leading-6 text-app-muted">{event.description}</p> : null}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {event?.location ? (
            <StatusPill tone="neutral" className="gap-1">
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
              <span>{event.location}</span>
            </StatusPill>
          ) : null}
          <StatusPill tone="neutral" className="gap-1">
            <Clock3 className="h-3.5 w-3.5" aria-hidden="true" />
            <span>{timeLabel || 'Scheduled'}</span>
          </StatusPill>
        </div>

        {event?.status !== 'completed' && event?.status !== 'cancelled' ? (
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              className="inline-flex items-center gap-2 rounded-xl bg-app-accent px-3 py-2 text-sm font-semibold text-white transition hover:bg-app-accent-strong"
              type="button"
              onClick={() => onUpdateEvent(eventId, { status: 'completed' })}
            >
              <CheckCheck className="h-4 w-4" aria-hidden="true" />
              <span>Mark completed</span>
            </button>
            <button
              className="inline-flex items-center gap-2 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-sm font-medium text-orange-700 transition hover:bg-orange-100"
              type="button"
              onClick={() => onUpdateEvent(eventId, { status: 'cancelled' })}
            >
              <XCircle className="h-4 w-4" aria-hidden="true" />
              <span>Cancel</span>
            </button>
          </div>
        ) : null}

        <div className="mt-5 flex flex-wrap gap-2 border-t border-app-border/60 pt-5">
          <button
            className="inline-flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-3 py-2 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
            type="button"
            onClick={() => onEditEvent(event)}
          >
            <PencilLine className="h-4 w-4" aria-hidden="true" />
            <span>Edit</span>
          </button>
          <button
            className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 transition hover:bg-red-100"
            type="button"
            onClick={() => onDeleteEvent(eventId)}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            <span>Delete</span>
          </button>
        </div>
      </div>
    </article>
  );
}

function CompactEventCard({ event, isFocused, onSelect }) {
  const { timeLabel } = formatEventDate(event?.startAt, event?.endAt, event?.allDay);
  const isPast = event?.status === 'completed' || event?.status === 'cancelled';

  return (
    <button
      className={cn(
        'w-full rounded-2xl border px-3 py-3 text-left shadow-subtle transition hover:-translate-y-0.5 hover:shadow-panel',
        isFocused
          ? 'border-app-accent/35 bg-blue-50'
          : event?.scope === 'family'
            ? 'border-emerald-200 bg-emerald-50/80'
            : 'border-blue-200 bg-blue-50/80',
        isPast && 'opacity-70'
      )}
      type="button"
      onClick={onSelect}
      aria-label={`Edit ${event?.title || 'Untitled event'}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-[11px] font-bold uppercase tracking-[0.12em] text-app-muted">
          {timeLabel || 'Scheduled'}
        </span>
        <span className={cn(
          'h-2 w-2 shrink-0 rounded-full',
          event?.status === 'completed' ? 'bg-emerald-500' :
          event?.status === 'cancelled' ? 'bg-red-500' :
          'bg-app-accent'
        )} />
      </div>
      <p className="mt-1 line-clamp-2 text-sm font-semibold leading-5 text-app-ink">
        {event?.title || 'Untitled event'}
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <span className="rounded-full bg-white/75 px-2 py-0.5 text-[11px] font-semibold text-app-muted">
          {event?.scope === 'family' ? 'Family' : 'Personal'}
        </span>
        {event?.allDay ? (
          <span className="rounded-full bg-white/75 px-2 py-0.5 text-[11px] font-semibold text-app-muted">
            All day
          </span>
        ) : null}
      </div>
    </button>
  );
}

function RoutineCard({ occurrence, onOpen, onComplete }) {
  const task = occurrence?.task || {};
  const time = formatEventDate(occurrence?.occurrenceAt, null, false).timeLabel;

  return (
    <article className="rounded-[22px] border border-emerald-200 bg-emerald-50/75 p-5 shadow-subtle">
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill tone="family">Routine</StatusPill>
        <StatusPill tone="neutral">{time || 'Any time'}</StatusPill>
        <StatusPill tone="neutral">{getRoutineTone(task)} reminder</StatusPill>
      </div>
      <p className="mt-3 text-lg font-semibold tracking-tight text-app-ink">{task.title || 'Untitled routine'}</p>
      {task.description ? <p className="mt-2 text-sm leading-6 text-app-muted">{task.description}</p> : null}
      <p className="mt-2 text-sm font-medium text-emerald-800">{formatRoutineRepeat(task.recurrence)}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-3 py-2 text-sm font-semibold text-white transition hover:bg-emerald-800"
          type="button"
          onClick={() => onComplete?.(task.id || task._id)}
        >
          <CheckCheck className="h-4 w-4" aria-hidden="true" />
          <span>Done for today</span>
        </button>
        <button
          className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm font-medium text-emerald-800 transition hover:bg-emerald-50"
          type="button"
          onClick={() => onOpen?.(task)}
        >
          <PencilLine className="h-4 w-4" aria-hidden="true" />
          <span>Open routine</span>
        </button>
      </div>
    </article>
  );
}

function WeekBoard({
  weekDays,
  eventsByDay,
  selectedDayKey,
  todayKey,
  calendarParams,
  onSelectDay,
  onSelectEvent,
}) {
  return (
    <div className="overflow-x-auto pb-2">
      <div className="grid min-w-[920px] grid-cols-7 gap-3">
        {weekDays.map((day) => {
          const dayEvents = eventsByDay.get(day.key) || [];
          const isSelected = selectedDayKey === day.key;
          const isToday = todayKey === day.key;

          return (
            <section
              key={day.key}
              className={cn(
                'min-h-[420px] rounded-[22px] border bg-app-surface/80 p-3 shadow-subtle transition',
                isSelected ? 'border-app-accent/35 ring-2 ring-app-accent/15' : 'border-app-border/70',
                isToday && 'bg-emerald-50/45'
              )}
            >
              <button
                type="button"
                className="w-full rounded-2xl px-2 py-2 text-left transition hover:bg-app-surface-alt/80"
                onClick={() => onSelectDay(day.key)}
                aria-pressed={isSelected}
                aria-label={`Select ${day.fullLabel}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="m-0 text-[11px] font-bold uppercase tracking-[0.18em] text-app-muted">
                      {day.weekdayLabel}
                    </p>
                    <p className="mt-1 text-2xl font-semibold tracking-tight text-app-ink">
                      {day.dayLabel}
                    </p>
                  </div>
                  <span className={cn(
                    'rounded-full border px-2 py-1 text-xs font-semibold',
                    dayEvents.length
                      ? 'border-app-accent/30 bg-blue-50 text-blue-700'
                      : 'border-app-border bg-app-surface-alt text-app-muted'
                  )}>
                    {dayEvents.length}
                  </span>
                </div>
                {isToday ? (
                  <span className="mt-2 inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                    Today
                  </span>
                ) : null}
              </button>

              <div className="mt-3 space-y-2">
                {dayEvents.length ? (
                  dayEvents.map((event) => {
                    const eventId = getEventId(event);
                    return (
                      <CompactEventCard
                        key={eventId || event?.title}
                        event={event}
                        isFocused={eventId && eventId === calendarParams?.focusEventId}
                        onSelect={() => {
                          onSelectDay(day.key);
                          onSelectEvent(event);
                        }}
                      />
                    );
                  })
                ) : (
                  <div className="rounded-2xl border border-dashed border-app-border/80 bg-app-surface-alt/55 px-3 py-5 text-center text-sm leading-6 text-app-muted">
                    Open space for plans.
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function WeekControlBar({
  calendarFilters,
  calendarStatus,
  weekStart,
  weekEnd,
  selectedDayKey,
  onNavigateWeek,
  onCalendarFilterChange,
  onRefresh,
}) {
  return (
    <SectionCard className="p-4 lg:p-5">
      <div className="flex flex-col gap-4 2xl:flex-row 2xl:items-center 2xl:justify-between">
        <div>
          <p className="m-0 text-xs font-semibold uppercase tracking-[0.18em] text-app-accent-strong">
            Week view
          </p>
          <h2 className="mt-1 text-2xl font-semibold tracking-tight text-app-ink">
            {formatWeekRange(weekStart, weekEnd)}
          </h2>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-1 rounded-2xl border border-app-border/80 bg-app-surface p-1 shadow-subtle">
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-app-muted transition hover:bg-app-surface-alt hover:text-app-ink"
              onClick={() => onNavigateWeek(addDays(weekStart, -7))}
              aria-label="Previous week"
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="rounded-xl px-4 py-2 text-sm font-semibold text-app-ink transition hover:bg-app-surface-alt"
              onClick={() => onNavigateWeek(new Date())}
            >
              This week
            </button>
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-app-muted transition hover:bg-app-surface-alt hover:text-app-ink"
              onClick={() => onNavigateWeek(addDays(weekStart, 7))}
              aria-label="Next week"
            >
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <label className="flex items-center gap-2 rounded-2xl border border-app-border/80 bg-app-surface px-3 py-2 text-sm font-medium text-app-muted shadow-subtle">
            <CalendarDays className="h-4 w-4" aria-hidden="true" />
            <span>Jump</span>
            <input
              type="date"
              value={selectedDayKey}
              onChange={(event) => {
                const date = parseDateKey(event.target.value);
                if (date) onNavigateWeek(date);
              }}
              className="w-[138px] rounded-xl border border-app-border bg-app-surface-alt px-2 py-1.5 text-sm font-semibold text-app-ink outline-none focus:ring-2 focus:ring-app-accent/25"
              aria-label="Jump to week"
            />
          </label>

          <SegmentedTabs
            value={calendarFilters.scope}
            onValueChange={(value) => onCalendarFilterChange('scope', value)}
            ariaLabel="Calendar scope filter"
            items={[
              { value: 'all', label: 'All' },
              { value: 'personal', label: 'Personal' },
              { value: 'family', label: 'Family' },
            ]}
          />

          <button
            className="inline-flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-4 py-3 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
            type="button"
            onClick={onRefresh}
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            <span>Refresh</span>
          </button>

          <StatusPill tone={calendarStatus.type === 'warning' ? 'warning' : calendarStatus.type}>
            {calendarStatus.message}
          </StatusPill>
        </div>
      </div>
    </SectionCard>
  );
}

function CalendarView({
  calendarStatus,
  calendarParams,
  calendarFilters,
  eventForm,
  eventScope,
  editingEventId,
  canUseFamilyScope,
  calendarData,
  routineTasks = [],
  onCalendarFilterChange,
  onCalendarFiltersChange,
  onRefresh,
  onCompleteRoutine,
  onOpenRoutine,
  onCreateEvent,
  onEventFormChange,
  onUpdateEvent,
  onEditEvent,
  onDeleteEvent,
  onCancelEventEdit,
}) {
  const [selectedDayKey, setSelectedDayKey] = useState(() => formatDateKey(new Date()));

  const rangeAnchor = parseDateKey(calendarFilters.startFrom) || parseDateKey(selectedDayKey) || new Date();
  const weekStart = getWeekStart(rangeAnchor);
  const weekEnd = addDays(weekStart, 6);
  const weekStartTime = weekStart.getTime();
  const weekStartKey = formatDateKey(weekStart);
  const weekEndKey = formatDateKey(weekEnd);
  const todayKey = formatDateKey(new Date());

  const weekDays = useMemo(() => {
    const weekdayFormatter = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
    const dayFormatter = new Intl.DateTimeFormat(undefined, { day: 'numeric' });
    const fullFormatter = new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });

    return Array.from({ length: 7 }, (_, index) => {
      const date = addDays(new Date(weekStartTime), index);
      return {
        date,
        key: formatDateKey(date),
        weekdayLabel: weekdayFormatter.format(date),
        dayLabel: dayFormatter.format(date),
        fullLabel: fullFormatter.format(date),
      };
    });
  }, [weekStartTime]);

  useEffect(() => {
    if (selectedDayKey < weekStartKey || selectedDayKey > weekEndKey) {
      setSelectedDayKey(weekStartKey);
    }
  }, [selectedDayKey, weekEndKey, weekStartKey]);

  const sortedEvents = useMemo(
    () =>
      [...calendarData].sort((first, second) => {
        const firstTime = getEventDate(first)?.getTime() || 0;
        const secondTime = getEventDate(second)?.getTime() || 0;
        return firstTime - secondTime;
      }),
    [calendarData]
  );

  const eventsByDay = useMemo(() => {
    const groupedEvents = new Map(weekDays.map((day) => [day.key, []]));
    sortedEvents.forEach((event) => {
      const key = getEventDateKey(event);
      if (groupedEvents.has(key)) {
        groupedEvents.get(key).push(event);
      }
    });
    return groupedEvents;
  }, [sortedEvents, weekDays]);

  const selectedDayEvents = eventsByDay.get(selectedDayKey) || [];
  const selectedDayRoutines = useMemo(
    () => projectRoutineTasksForDay(routineTasks, selectedDayKey),
    [routineTasks, selectedDayKey]
  );
  const weekEventCount = weekDays.reduce((total, day) => total + (eventsByDay.get(day.key)?.length || 0), 0);
  const upcomingCount = calendarData.filter(
    (event) =>
      event.status !== 'cancelled' &&
      event.status !== 'completed' &&
      new Date(event.startAt) >= new Date()
  ).length;
  const todayCount =
    calendarData.filter((event) => getEventDateKey(event) === todayKey).length +
    projectRoutineTasksForDay(routineTasks, todayKey).length;
  const pastCount = calendarData.filter(
    (event) =>
      event.status === 'completed' || (event.startAt && new Date(event.startAt) < new Date())
  ).length;
  const selectedDayLabel = weekDays.find((day) => day.key === selectedDayKey)?.fullLabel || 'Selected day';

  const navigateToWeek = (date) => {
    const nextWeekStart = getWeekStart(date);
    const nextWeekEnd = addDays(nextWeekStart, 6);
    const nextDayKey = formatDateKey(startOfDay(date));
    setSelectedDayKey(nextDayKey);

    const updates = {
      startFrom: formatDateKey(nextWeekStart),
      startTo: formatDateKey(nextWeekEnd),
    };

    if (onCalendarFiltersChange) {
      onCalendarFiltersChange(updates);
    } else {
      onCalendarFilterChange('startFrom', updates.startFrom);
      onCalendarFilterChange('startTo', updates.startTo);
    }
  };

  return (
    <section className="w-full max-w-[1680px] text-app-ink">
      <PageHero
        eyebrow="Calendar"
        title="Calendar navigation"
        lede="A week board for personal and family events, with filters and event creation tucked into a compact control rail."
        bearyMessage={calendarStatus.type === 'loading' ? 'Beary is loading calendar events.' : undefined}
        bearyPreset="loading"
      />

      <div className="mt-6 grid gap-4 sm:grid-cols-4">
        <MetricCard label="This week" value={weekEventCount} icon={CalendarRange} accentClassName="text-blue-700" />
        <MetricCard label="Upcoming" value={upcomingCount} icon={CalendarDays} accentClassName="text-app-ink" />
        <MetricCard label="Today" value={todayCount} icon={Clock3} accentClassName="text-app-accent-strong" />
        <MetricCard label="Past" value={pastCount} icon={CheckCheck} accentClassName="text-emerald-700" />
      </div>

      <div className="mt-6 grid gap-6 2xl:grid-cols-[minmax(0,1fr)_390px]">
        <div className="min-w-0 space-y-6">
          <WeekControlBar
            calendarFilters={calendarFilters}
            calendarStatus={calendarStatus}
            weekStart={weekStart}
            weekEnd={weekEnd}
            selectedDayKey={selectedDayKey}
            onNavigateWeek={navigateToWeek}
            onCalendarFilterChange={onCalendarFilterChange}
            onRefresh={onRefresh}
          />

          <SectionCard className="p-4 lg:p-5">
            <SectionTitle
              title="Week board"
              copy={
                calendarFilters.scope !== 'all'
                  ? `Showing ${calendarFilters.scope} events for the active week.`
                  : 'All visible personal and family events for the active week.'
              }
              action={<StatusPill tone="neutral">{weekEventCount} this week</StatusPill>}
            />
            <div className="mt-5">
              <WeekBoard
                weekDays={weekDays}
                eventsByDay={eventsByDay}
                selectedDayKey={selectedDayKey}
                todayKey={todayKey}
                calendarParams={calendarParams}
                onSelectDay={setSelectedDayKey}
                onSelectEvent={onEditEvent}
              />
            </div>
          </SectionCard>

          <SectionCard>
            <SectionTitle
              title={`${selectedDayLabel} agenda`}
              copy="Schedule shows places to be; routines show what needs doing today."
              action={<StatusPill tone="neutral">{selectedDayEvents.length + selectedDayRoutines.length}</StatusPill>}
            />

            {selectedDayEvents.length || selectedDayRoutines.length ? (
              <div className="mt-5 space-y-6">
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-app-accent-strong" aria-hidden="true" />
                    <h3 className="m-0 text-sm font-bold uppercase tracking-[0.14em] text-app-muted">Schedule</h3>
                  </div>
                  {selectedDayEvents.length ? (
                    <div className="grid gap-4 xl:grid-cols-2">
                {selectedDayEvents.map((event) => (
                  <EventCard
                    key={getEventId(event) || event?.title}
                    event={event}
                    calendarParams={calendarParams}
                    onUpdateEvent={onUpdateEvent}
                    onEditEvent={onEditEvent}
                    onDeleteEvent={onDeleteEvent}
                  />
                ))}
                    </div>
                  ) : (
                    <EmptyState title="No schedule on this day." copy="Calendar events will appear here." className="py-8 lg:py-8" />
                  )}
                </div>

                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <Repeat2 className="h-4 w-4 text-emerald-700" aria-hidden="true" />
                    <h3 className="m-0 text-sm font-bold uppercase tracking-[0.14em] text-app-muted">Routines</h3>
                  </div>
                  {selectedDayRoutines.length ? (
                    <div className="grid gap-4 xl:grid-cols-2">
                      {selectedDayRoutines.map((occurrence) => (
                        <RoutineCard
                          key={`${occurrence.task.id || occurrence.task._id}-${occurrence.occurrenceAt}`}
                          occurrence={occurrence}
                          onOpen={onOpenRoutine}
                          onComplete={onCompleteRoutine}
                        />
                      ))}
                    </div>
                  ) : (
                    <EmptyState title="No routines on this day." copy="Tagged recurring family tasks will appear here." className="py-8 lg:py-8" />
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-5">
                <EmptyState
                  title="No events on this day."
                  copy="Select another day, change the week range, or create a new event from the control rail."
                />
              </div>
            )}
          </SectionCard>
        </div>

        <aside className="space-y-6 2xl:sticky 2xl:top-24 2xl:self-start">
          <SectionCard>
            <SectionTitle
              title="Filters"
              copy="The week controls update the same date filters used by the backend."
            />

            <div className="mt-5 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink" htmlFor="calendar-status">
                  Status
                </label>
                <select
                  id="calendar-status"
                  value={calendarFilters.status}
                  onChange={(event) => onCalendarFilterChange('status', event.target.value)}
                  className={inputClassName}
                >
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="all">All statuses</option>
                </select>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-1">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-app-ink" htmlFor="calendar-start-from">
                    Start from
                  </label>
                  <input
                    id="calendar-start-from"
                    type="date"
                    value={calendarFilters.startFrom}
                    onChange={(event) => {
                      onCalendarFilterChange('startFrom', event.target.value);
                      if (event.target.value) setSelectedDayKey(event.target.value);
                    }}
                    className={inputClassName}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-app-ink" htmlFor="calendar-start-to">
                    Start to
                  </label>
                  <input
                    id="calendar-start-to"
                    type="date"
                    value={calendarFilters.startTo}
                    onChange={(event) => onCalendarFilterChange('startTo', event.target.value)}
                    className={inputClassName}
                  />
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard>
            <SectionTitle
              title={editingEventId ? 'Edit event' : 'New event'}
              copy={
                editingEventId
                  ? 'Update the selected calendar entry in place.'
                  : 'Capture a plan with time, place, and notes.'
              }
            />

            <form className="mt-5 space-y-4" onSubmit={onCreateEvent}>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink" htmlFor="event-title">
                  Title
                </label>
                <input
                  id="event-title"
                  value={eventForm.title}
                  onChange={(event) => onEventFormChange('title', event.target.value)}
                  placeholder="Parent teacher conference"
                  required
                  className={inputClassName}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink" htmlFor="event-description">
                  Description
                </label>
                <textarea
                  id="event-description"
                  rows="3"
                  value={eventForm.description}
                  onChange={(event) => onEventFormChange('description', event.target.value)}
                  placeholder="Anything the family needs to know."
                  className={inputClassName}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink" htmlFor="event-location">
                  Location
                </label>
                <input
                  id="event-location"
                  value={eventForm.location}
                  onChange={(event) => onEventFormChange('location', event.target.value)}
                  placeholder="School library"
                  className={inputClassName}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-1">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-app-ink" htmlFor="event-start-at">
                    Start at
                  </label>
                  <input
                    id="event-start-at"
                    type="datetime-local"
                    value={eventForm.startAt}
                    onChange={(event) => onEventFormChange('startAt', event.target.value)}
                    className={inputClassName}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-app-ink" htmlFor="event-end-at">
                    End at
                  </label>
                  <input
                    id="event-end-at"
                    type="datetime-local"
                    value={eventForm.endAt}
                    onChange={(event) => onEventFormChange('endAt', event.target.value)}
                    className={inputClassName}
                  />
                </div>
              </div>

              <label className="flex items-center gap-3 rounded-xl border border-app-border/70 bg-app-surface-alt/70 px-4 py-3 text-sm font-medium text-app-ink">
                <input
                  type="checkbox"
                  checked={eventForm.allDay}
                  onChange={(event) => onEventFormChange('allDay', event.target.checked)}
                />
                <span>All day</span>
              </label>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink">Scope</label>
                {editingEventId ? (
                  <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4 text-sm text-app-muted">
                    <p className="m-0 font-semibold text-app-ink">
                      {eventScope === 'family' ? 'Family shared' : 'Personal'}
                    </p>
                    <p className="mt-1">Scope stays fixed while editing an existing event.</p>
                  </div>
                ) : (
                  <>
                    <SegmentedTabs
                      value={eventScope}
                      onValueChange={(value) => onCalendarFilterChange('eventScope', value)}
                      ariaLabel="Event scope"
                      items={[
                        { value: 'personal', label: 'Personal' },
                        { value: 'family', label: 'Family', disabled: !canUseFamilyScope },
                      ]}
                    />
                    {!canUseFamilyScope ? (
                      <p className="text-sm leading-6 text-app-muted">
                        Open Family to create a shared household before adding family events.
                      </p>
                    ) : null}
                  </>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink" htmlFor="event-tags">
                  Tags
                </label>
                <input
                  id="event-tags"
                  value={eventForm.tags}
                  onChange={(event) => onEventFormChange('tags', event.target.value)}
                  placeholder="school, logistics"
                  className={inputClassName}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-app-ink">Recurrence</label>
                <RecurrenceEditor
                  value={eventForm.recurrence}
                  onChange={(value) => onEventFormChange('recurrence', value)}
                  idPrefix="event-recurrence"
                  requireDateCopy="A recurring event needs a start time. Generated occurrences will appear in the calendar board."
                />
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  className="inline-flex items-center justify-center rounded-xl bg-app-accent px-4 py-3 text-sm font-semibold text-white transition hover:bg-app-accent-strong"
                  type="submit"
                >
                  {editingEventId ? 'Save event' : 'Create event'}
                </button>
                {editingEventId ? (
                  <button
                    className="inline-flex items-center justify-center rounded-xl border border-app-border bg-app-surface px-4 py-3 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
                    type="button"
                    onClick={onCancelEventEdit}
                  >
                    Cancel edit
                  </button>
                ) : null}
              </div>
            </form>
          </SectionCard>
        </aside>
      </div>
    </section>
  );
}

export default CalendarView;
